import { from } from '@phi/newtype';
import { Vector3 } from 'three';
import { check_that_user_subscription_is_valid } from '../user_authentication';
import * as logged_jcore from '../JCore/jcore';
import { CoreFunctionCallDescription } from '@phi/logging/core-function-descriptions';
import { WrappedValue } from '@phi/wrapped-value';
import { uid_get_ball_hardness, uid_get_ball_size } from '../ui_display';
import { rgbToHex } from '../colours_materials_appearance';
import { console_log_xml } from '../utility';
import { logged_jcore_do_magic } from '../debug';
import type { Body, Edge, Face, Vertex } from '@phi/jcore';

/** Flag for controlling whether we log 'action' API calls,
 * i.e. calls that result in changes to the core data structures
 */
const LOGGING_ACTION_ON = true;

/** Flag for controlling whether we log 'info' API calls,
 * i.e. calls that only read and return infromation
 * from the core data structures
 */
const LOGGING_INFO_ON = false;

export function init(is_first_init: boolean) {
    //Allow core to load
    //if (!check_that_user_subscription_is_valid())
    //  return;
    return logged_jcore.init(is_first_init);
}

/*
function jcore_load_default_model() {
  // Not logging this function.
  return logged_jcore.load_default_model();
}
*/

export function load_model(str: string, into_current: boolean, additional_scale_factor: number, no_id_rehash: boolean) {
    //Allow loading model even if expireds
    //if (!check_that_user_subscription_is_valid())
    //  return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_load_model');
        f.add_parameter(str);
        f.add_parameter(str.length);
        f.add_parameter(into_current ? 1 : 0);
        f.add_parameter(additional_scale_factor);
        f.add_parameter(0);
        f.add_parameter(no_id_rehash ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.load_model(str, into_current, additional_scale_factor, no_id_rehash);
}

export function start_move_vertices(vertex_ids: Array<Vertex>) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_start_move_vertices');
        f.add_parameter(vertex_ids.length);
        for (const vid of vertex_ids)
            f.add_parameter(from(vid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.start_move_vertices(vertex_ids);
}

export function move_vertices(vertex_ids: Array<Vertex>, origin: Vector3, direction: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (origin == null || direction == null || isNaN(origin.x) || isNaN(origin.y) || isNaN(origin.z) || isNaN(direction.x) || isNaN(direction.y) || isNaN(direction.z)) {
        console_log_xml('ERROR: BAD input for phi_move_vertices');
        // @ts-ignore
        console.everything.push({
            'type': 'error',
            'datetime': Date().toLocaleString(),
            'value': [vertex_ids, origin, direction]
        });
        // @ts-ignore
        console.defaultError([vertex_ids, origin, direction]);
        return 0;
    }

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_move_vertices');
        f.add_parameter(vertex_ids.length);
        for (const vid of vertex_ids)
            f.add_parameter(from(vid));
        f.add_parameter(origin.x);
        f.add_parameter(origin.y);
        f.add_parameter(origin.z);
        f.add_parameter(direction.x);
        f.add_parameter(direction.y);
        f.add_parameter(direction.z);
        f.add_parameter(uid_get_ball_size());
        f.add_parameter(uid_get_ball_hardness());
        cfcd.add_function_call(f);
    }
    return logged_jcore.move_vertices(vertex_ids, origin, direction);
}

export function end_move_vertices(vertex_ids: Array<Vertex>) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_end_move_vertices');
        f.add_parameter(vertex_ids.length);
        for (const vid of vertex_ids)
            f.add_parameter(from(vid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.end_move_vertices(vertex_ids);
}

export function start_deform_edge(id: Edge) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_start_deform_edge');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.start_deform_edge(id);
}

export function deform_edge(id: Edge, origin: Vector3, direction: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_deform_edge');
        f.add_parameter(from(id));
        f.add_parameter(origin.x);
        f.add_parameter(origin.y);
        f.add_parameter(origin.z);
        f.add_parameter(direction.x);
        f.add_parameter(direction.y);
        f.add_parameter(direction.z);
        f.add_parameter(uid_get_ball_size());
        f.add_parameter(uid_get_ball_hardness());
        cfcd.add_function_call(f);
    }
    return logged_jcore.deform_edge(id, origin, direction);
}

export function end_deform_edge(id: Edge) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_end_deform_edge');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.end_deform_edge(id);
}

export function start_move_vertex_on_edge(vertex_id: Vertex, edge_id: Edge) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_start_move_vertex_on_edge');
        f.add_parameter(from(vertex_id));
        f.add_parameter(from(edge_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.start_move_vertex_on_edge(vertex_id, edge_id);
}

export function move_vertex_on_edge(vertex_id: Vertex, edge_id: Edge, origin: Vector3, direction: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_move_vertex_on_edge');
        f.add_parameter(from(vertex_id));
        f.add_parameter(from(edge_id));
        f.add_parameter(origin.x);
        f.add_parameter(origin.y);
        f.add_parameter(origin.z);
        f.add_parameter(direction.x);
        f.add_parameter(direction.y);
        f.add_parameter(direction.z);
        f.add_parameter(uid_get_ball_size());
        f.add_parameter(uid_get_ball_hardness());
        cfcd.add_function_call(f);
    }
    return logged_jcore.move_vertex_on_edge(vertex_id, edge_id, origin, direction);
}

export function end_move_vertex_on_edge(vertex_id: Vertex, edge_id: Edge) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_end_move_vertex_on_edge');
        f.add_parameter(from(vertex_id));
        f.add_parameter(from(edge_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.end_move_vertex_on_edge(vertex_id, edge_id);
}

export function get_edge_list_geometry(edge_ids: Array<Edge>, is_high_res: boolean, centre_coords: Vector3, norm_coords: Vector3, wv_radius: WrappedValue<number>, characteristic_dir_coords: Vector3) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_geometry_of_edges');
        f.add_parameter(edge_ids.length);
        for (const eid of edge_ids)
            f.add_parameter(from(eid));
        f.add_parameter(is_high_res ? 1 : 0);
        f.add_parameter(centre_coords.x);
        f.add_parameter(centre_coords.y);
        f.add_parameter(centre_coords.z);
        f.add_parameter(norm_coords.x);
        f.add_parameter(norm_coords.y);
        f.add_parameter(norm_coords.z);
        f.add_parameter(wv_radius.v);
        f.add_parameter(characteristic_dir_coords.x);
        f.add_parameter(characteristic_dir_coords.y);
        f.add_parameter(characteristic_dir_coords.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edge_list_geometry(edge_ids, is_high_res, centre_coords, norm_coords, wv_radius, characteristic_dir_coords);
}

export function get_face_geometry(face_id: Face, centre_coords: Vector3, norm_coords: Vector3, wv_radius: WrappedValue<number>, characteristic_dir_coords: Vector3, wv_flatness: WrappedValue<number>) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_geometry');
        f.add_parameter(from(face_id));
        f.add_parameter(centre_coords.x);
        f.add_parameter(centre_coords.y);
        f.add_parameter(centre_coords.z);
        f.add_parameter(norm_coords.x);
        f.add_parameter(norm_coords.y);
        f.add_parameter(norm_coords.z);
        f.add_parameter(wv_radius.v);
        f.add_parameter(characteristic_dir_coords.x);
        f.add_parameter(characteristic_dir_coords.y);
        f.add_parameter(characteristic_dir_coords.z);
        f.add_parameter(wv_flatness.v);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_geometry(face_id, centre_coords, norm_coords, wv_radius, characteristic_dir_coords, wv_flatness);
}

export function listify_edges(input_edge_ids: Array<Edge>, wv_is_open_list: WrappedValue<number>, output_edge_ids: Array<Edge>) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_listify_edges');
        f.add_parameter(input_edge_ids.length);
        for (const id of input_edge_ids)
            f.add_parameter(from(id));
        f.add_parameter(0);
        f.add_parameter(0);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }

    return logged_jcore.listify_edges(input_edge_ids, wv_is_open_list, output_edge_ids);
}

export function get_face_connected_component_info(
    input_face_ids: Array<Face>,
    input_edge_ids: Array<Edge>,
    input_vertex_ids: Array<Vertex>,
    wv_is_one_cc: WrappedValue<boolean>,
    output_boundary_edge_ids: Array<Edge>,
    output_boundary_vertex_ids: Array<Vertex>,
) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_connected_component_info');
        f.add_parameter(input_face_ids.length);
        for (const id of input_face_ids)
            f.add_parameter(from(id));
        f.add_parameter(0);
        f.add_parameter(0);
        f.add_parameter(0);
        f.add_parameter(0);
        f.add_parameter(0);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }

    return logged_jcore.get_face_connected_component_info(input_face_ids, input_edge_ids, input_vertex_ids, wv_is_one_cc, output_boundary_edge_ids, output_boundary_vertex_ids);
}

export function get_face_connected_components(input_face_ids: Array<Face>, output_connected_component_array: Array<Array<Face>>) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_connected_components');
        f.add_parameter(input_face_ids.length);
        for (const id of input_face_ids)
            f.add_parameter(from(id));
        f.add_parameter(0);
        f.add_parameter(0);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }

    return logged_jcore.get_face_connected_components(input_face_ids, output_connected_component_array);
}

export function get_ray_edge_intersection(eid: Edge, ray_base: Vector3, ray_direction: Vector3, is_high_res: boolean, wv_parameter: WrappedValue<number>) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_ray_edge_intersection');
        f.add_parameter(from(eid));
        f.add_parameter(ray_base.x);
        f.add_parameter(ray_base.y);
        f.add_parameter(ray_base.z);
        f.add_parameter(ray_direction.x);
        f.add_parameter(ray_direction.y);
        f.add_parameter(ray_direction.z);
        f.add_parameter(is_high_res ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_ray_edge_intersection(eid, ray_base, ray_direction, is_high_res, wv_parameter);
}

export function get_face_ray_intersection(fid: Face, ray_base: Vector3, ray_direction: Vector3, wv_u: WrappedValue<number>, wv_v: WrappedValue<number>) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_ray_intersection');
        f.add_parameter(from(fid));
        f.add_parameter(ray_base.x);
        f.add_parameter(ray_base.y);
        f.add_parameter(ray_base.z);
        f.add_parameter(ray_direction.x);
        f.add_parameter(ray_direction.y);
        f.add_parameter(ray_direction.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_ray_intersection(fid, ray_base, ray_direction, wv_u, wv_v);
}

export function get_face_normal_at_edge_point(fid: Face, eid: Edge, ray_base: Vector3, ray_direction: Vector3) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_normal_at_edge_point');
        f.add_parameter(from(fid));
        f.add_parameter(from(eid));
        f.add_parameter(ray_base.x);
        f.add_parameter(ray_base.y);
        f.add_parameter(ray_base.z);
        f.add_parameter(ray_direction.x);
        f.add_parameter(ray_direction.y);
        f.add_parameter(ray_direction.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_normal_at_edge_point(fid, eid, ray_base, ray_direction);
}

export function start_move_edge_list(object_ids: Array<Edge>) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_start_move_edges');
        f.add_parameter(object_ids.length);
        for (const id of object_ids)
            f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.start_move_edge_list(object_ids);
}

export function move_edge_list(
    object_ids: Array<Edge>,
    translation_vector: Vector3,
    rotation_angle: number,
    rotation_axis_base: Vector3,
    rotation_axis_direction: Vector3,
) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_move_edges');
        f.add_parameter(object_ids.length);
        for (const id of object_ids)
            f.add_parameter(from(id));
        f.add_parameter(translation_vector.x);
        f.add_parameter(translation_vector.y);
        f.add_parameter(translation_vector.z);
        f.add_parameter(rotation_axis_base.x);
        f.add_parameter(rotation_axis_base.y);
        f.add_parameter(rotation_axis_base.z);
        f.add_parameter(rotation_axis_direction.x);
        f.add_parameter(rotation_axis_direction.y);
        f.add_parameter(rotation_axis_direction.z);
        f.add_parameter(rotation_angle);
        //f.add_parameter(ball_size);
        //f.add_parameter(ball_hardness);
        cfcd.add_function_call(f);
    }
    return logged_jcore.move_edge_list(object_ids, translation_vector, rotation_angle, rotation_axis_base, rotation_axis_direction);
}

export function end_move_edge_list(object_ids: Array<Edge>) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_end_move_edges');
        f.add_parameter(object_ids.length);
        for (const id of object_ids)
            f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.end_move_edge_list(object_ids);
}

export function start_rotate_vertex_normal(id: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_start_rotate_vertex_normal');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }

    return logged_jcore.start_rotate_vertex_normal(id);
}

export function rotate_vertex_normal(id: Vertex, direction: Vector3, angle: number) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_rotate_vertex_normal');
        f.add_parameter(from(id));
        f.add_parameter(direction.x);
        f.add_parameter(direction.y);
        f.add_parameter(direction.z);
        f.add_parameter(angle);
        cfcd.add_function_call(f);
    }

    return logged_jcore.rotate_vertex_normal(id, direction, angle);
}

export function end_rotate_vertex_normal(id: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_end_rotate_vertex_normal');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.end_rotate_vertex_normal(id);
}

export function start_rotate_edge_tangent(eid: Edge, vid: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_start_rotate_edge_tangent');
        f.add_parameter(from(eid));
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.start_rotate_edge_tangent(eid, vid);
}

export function rotate_edge_tangent(eid: Edge, vid: Vertex, direction: Vector3, angle: number) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_rotate_edge_tangent');
        f.add_parameter(from(eid));
        f.add_parameter(from(vid));
        f.add_parameter(direction.x);
        f.add_parameter(direction.y);
        f.add_parameter(direction.z);
        f.add_parameter(angle);
        cfcd.add_function_call(f);
    }
    return logged_jcore.rotate_edge_tangent(eid, vid, direction, angle);
}

export function end_rotate_edge_tangent(eid: Edge, vid: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_end_rotate_edge_tangent');
        f.add_parameter(from(eid));
        f.add_parameter(from(vid));
        cfcd.add_function_call(f);
    }

    return logged_jcore.end_rotate_edge_tangent(eid, vid);
}

export function start_scale_edge_list(scale_edge_objects: Array<Edge>) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_start_scale_edges');
        f.add_parameter(scale_edge_objects.length);
        for (const id of scale_edge_objects)
            f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }

    return logged_jcore.start_scale_edge_list(scale_edge_objects);
}

export function scale_edge_list(
    scale_edge_objects: Array<Edge>,
    scale_center: Vector3,
    scale_factor: number,
    use_axis: boolean,
    axis: Vector3,
) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_scale_edges');
        f.add_parameter(scale_edge_objects.length);
        for (const id of scale_edge_objects)
            f.add_parameter(from(id));
        f.add_parameter(scale_center.x);
        f.add_parameter(scale_center.y);
        f.add_parameter(scale_center.z);
        f.add_parameter(scale_factor);
        f.add_parameter(use_axis ? 1 : 0);
        f.add_parameter(axis.x);
        f.add_parameter(axis.y);
        f.add_parameter(axis.z);
        cfcd.add_function_call(f);
    }

    return logged_jcore.scale_edge_list(
        scale_edge_objects,
        scale_center,
        scale_factor,
        use_axis,
        axis,
    );
}

export function end_scale_edge_list(scale_edge_objects: Array<Edge>) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_end_scale_edges');
        f.add_parameter(scale_edge_objects.length);
        for (const id of scale_edge_objects)
            f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }

    return logged_jcore.end_scale_edge_list(scale_edge_objects);
}

export function start_offset_face(face_id: Face, vertex_id: Vertex, point_coordinates: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_start_face_offset');
        f.add_parameter(from(face_id));
        f.add_parameter(from(vertex_id));
        f.add_parameter(point_coordinates.x);
        f.add_parameter(point_coordinates.y);
        f.add_parameter(point_coordinates.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.start_offset_face(face_id, vertex_id, point_coordinates);
}

export function offset_face_drag_step(face_id: Face, vertex_id: Vertex, point_coordinates: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_face_offset');
        f.add_parameter(from(face_id));
        f.add_parameter(from(vertex_id));
        f.add_parameter(point_coordinates.x);
        f.add_parameter(point_coordinates.y);
        f.add_parameter(point_coordinates.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.offset_face_drag_step(face_id, vertex_id, point_coordinates);
}

export function end_offset_face(face_id: Face, vertex_id: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_end_face_offset');
        f.add_parameter(from(face_id));
        f.add_parameter(from(vertex_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.end_offset_face(face_id, vertex_id);
}

export function set_vertex_distance(v1id: Vertex, v2id: Vertex, dist: number) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_vertex_distance');
        f.add_parameter(from(v1id));
        f.add_parameter(from(v2id));
        f.add_parameter(dist);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_vertex_distance(v1id, v2id, dist);
}

export function is_vertex_smooth(vid: Vertex) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_vertex_smooth');
        f.add_parameter(from(vid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_vertex_smooth(vid);
}

export function is_joint_smooth(e1id: Edge, e2id: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_joint_smooth');
        f.add_parameter(from(e1id));
        f.add_parameter(from(e2id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_joint_smooth(e1id, e2id);
}

export function is_joint_geom_smooth(e1id: Edge, e2id: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_joint_geom_smooth');
        f.add_parameter(from(e1id));
        f.add_parameter(from(e2id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_joint_geom_smooth(e1id, e2id);
}

export function delete_body(body_id: Body) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_delete_body');
        f.add_parameter(from(body_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.delete_body(body_id);
}


export function delete_objects(object_ids: Array<Vertex | Edge | Face>) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (object_ids.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_delete_objects');
        f.add_parameter(object_ids.length);
        for (const id of object_ids)
            f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.delete_objects(object_ids);
}

export function delete_faces(object_ids: Array<Face>) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (object_ids.length == 0)
        return true;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_delete_faces');
        f.add_parameter(object_ids.length);
        for (const id of object_ids)
            f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.delete_faces(object_ids);
}

export function delete_vertex(id: Vertex, no_geometry_change = false) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_delete_vertex');
        f.add_parameter(from(id));
        f.add_parameter(no_geometry_change ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.delete_vertex(id, no_geometry_change);
}

export function add_vertex(edge_id: Edge, ray_base: Vector3, ray_direction: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_add_vertex');
        f.add_parameter(from(edge_id));
        f.add_parameter(ray_base.x);
        f.add_parameter(ray_base.y);
        f.add_parameter(ray_base.z);
        f.add_parameter(ray_direction.x);
        f.add_parameter(ray_direction.y);
        f.add_parameter(ray_direction.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.add_vertex(edge_id, ray_base, ray_direction);
}

export function add_vertex_at_param(edge_id: Edge, t: number) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_add_vertex_at_param');
        f.add_parameter(from(edge_id));
        f.add_parameter(t);
        cfcd.add_function_call(f);
    }
    return logged_jcore.add_vertex_at_param(edge_id, t);
}

export function add_edge(face_id: Face, vertex1_id: Vertex, vertex2_id: Vertex, sample_points: Vector3[], sample_normals: Vector3[], is_planar: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_add_edge');
        f.add_parameter(from(face_id));
        f.add_parameter(from(vertex1_id));
        f.add_parameter(from(vertex2_id));
        f.add_parameter(sample_points.length);
        for (const p of sample_points) {
            f.add_parameter(p.x);
            f.add_parameter(p.y);
            f.add_parameter(p.z);
        }
        for (const n of sample_normals) {
            f.add_parameter(n.x);
            f.add_parameter(n.y);
            f.add_parameter(n.z);
        }
        f.add_parameter(is_planar ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.add_edge(face_id, vertex1_id, vertex2_id, sample_points, sample_normals, is_planar);
}

export function modify_edge_geometry(edge_id: Edge, vertex1_id: Vertex, vertex2_id: Vertex, samples: Vector3[]) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_modify_edge_geometry');
        f.add_parameter(from(edge_id));
        f.add_parameter(from(vertex1_id));
        f.add_parameter(from(vertex2_id));
        f.add_parameter(samples.length);
        for (const p of samples) {
            f.add_parameter(p.x);
            f.add_parameter(p.y);
            f.add_parameter(p.z);
        }
        cfcd.add_function_call(f);
    }
    return logged_jcore.modify_edge_geometry(edge_id, vertex1_id, vertex2_id, samples);
}

export function add_joint_smoothness(e1id: Edge, e2id: Edge, strength: number, propagate_to_symmetry: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_add_joint_smoothness');
        f.add_parameter(from(e1id));
        f.add_parameter(from(e2id));
        f.add_parameter(strength);
        cfcd.add_function_call(f);
    }
    return logged_jcore.add_joint_smoothness(e1id, e2id, strength, propagate_to_symmetry);
}

export function remove_joint_smoothness(e1id: Edge, e2id: Edge, no_geometry_change: boolean, propagate_to_symmetry: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_remove_joint_smoothness');
        f.add_parameter(from(e1id));
        f.add_parameter(from(e2id));
        f.add_parameter(no_geometry_change ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.remove_joint_smoothness(e1id, e2id, no_geometry_change, propagate_to_symmetry);
}

export function can_make_edge_smooth(eid: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_make_edge_smooth');
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_make_edge_smooth(eid);
}

export function can_make_edge_nonsmooth(eid: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_make_edge_nonsmooth');
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_make_edge_nonsmooth(eid);
}

export function make_edge_smooth(eid: Edge, radius: number) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_make_edge_smooth');
        f.add_parameter(from(eid));
        f.add_parameter(radius);
        cfcd.add_function_call(f);
    }
    return logged_jcore.make_edge_smooth(eid, radius);
}

export function make_edge_nonsmooth(eid: Edge) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_make_edge_nonsmooth');
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.make_edge_nonsmooth(eid);
}

export function smoothen_vertices(vertex_list: Array<Vertex>, radius: number) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (vertex_list.length == 0)
        return true;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_smoothen_vertices');
        f.add_parameter(vertex_list.length);
        for (const vid of vertex_list)
            f.add_parameter(from(vid));
        f.add_parameter(radius);
        cfcd.add_function_call(f);
    }
    return logged_jcore.smoothen_vertices(vertex_list, radius);
}

export function remove_vertex_smoothness(vertex_list: Array<Vertex>) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (vertex_list.length == 0)
        return true;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_remove_vertex_smoothness');
        f.add_parameter(vertex_list.length);
        for (const vid of vertex_list)
            f.add_parameter(from(vid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.remove_vertex_smoothness(vertex_list);
}

export function smoothen_edge_wrt_face(eid: Edge, fid: Face, vid: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_smoothen_edge_wrt_face');
        f.add_parameter(from(eid));
        f.add_parameter(from(fid));
        f.add_parameter(from(vid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.smoothen_edge_wrt_face(eid, fid, vid);
}

export function smoothen_edges(ids_of_edges_to_smoothen: Array<Edge>, strength: number, force_fix_1st_ders: boolean, force_fix_2nd_ders: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (ids_of_edges_to_smoothen.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_smoothen_edges');
        f.add_parameter(ids_of_edges_to_smoothen.length);
        for (const id of ids_of_edges_to_smoothen)
            f.add_parameter(from(id));
        f.add_parameter(strength);
        f.add_parameter(force_fix_1st_ders ? 1 : 0);
        f.add_parameter(force_fix_2nd_ders ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.smoothen_edges(ids_of_edges_to_smoothen, strength, force_fix_1st_ders, force_fix_2nd_ders);
}

export function smoothen_edges_as_one(
    ids_of_edges_to_smoothen: Array<Edge>,
    strength: number,
    strictly_fix_end_ders: boolean,
    fix_end_ders_override = false,
) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (ids_of_edges_to_smoothen.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_smoothen_edges_as_one');
        f.add_parameter(ids_of_edges_to_smoothen.length);
        for (const id of ids_of_edges_to_smoothen)
            f.add_parameter(from(id));
        f.add_parameter(strength);
        f.add_parameter(strictly_fix_end_ders ? 1 : 0);
        f.add_parameter(fix_end_ders_override ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.smoothen_edges_as_one(ids_of_edges_to_smoothen, strength, strictly_fix_end_ders, fix_end_ders_override);
}

export function can_smoothen_edges_as_one(ids_of_edges_to_smoothen: Array<Edge>) {
    if (ids_of_edges_to_smoothen.length == 0)
        return true;
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_smoothen_edges_as_one');
        f.add_parameter(ids_of_edges_to_smoothen.length);
        for (const id of ids_of_edges_to_smoothen)
            f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_smoothen_edges_as_one(ids_of_edges_to_smoothen);
}

export function smoothen_chain_network(ids_of_edges_to_smoothen: Array<Edge>, graph_distance: number, strength: number) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (ids_of_edges_to_smoothen.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_smoothen_chain_network');
        f.add_parameter(ids_of_edges_to_smoothen.length);
        for (const id of ids_of_edges_to_smoothen)
            f.add_parameter(from(id));
        f.add_parameter(graph_distance);
        f.add_parameter(strength);
        cfcd.add_function_call(f);
    }
    return logged_jcore.smoothen_chain_network(ids_of_edges_to_smoothen, graph_distance, strength);
}

export function make_edges_linear_as_one(ids_of_edges_to_make_linear: Array<Edge>, force: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (ids_of_edges_to_make_linear.length == 0)
        return true;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_make_edges_linear_as_one');
        f.add_parameter(ids_of_edges_to_make_linear.length);
        for (const id of ids_of_edges_to_make_linear)
            f.add_parameter(from(id));
        f.add_parameter(force ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.make_edges_linear_as_one(ids_of_edges_to_make_linear, force);
}

export function inflate_face(fid: Face, nsteps: number) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_inflate_face');
        f.add_parameter(from(fid));
        f.add_parameter(nsteps);
        cfcd.add_function_call(f);
    }
    return logged_jcore.inflate_face(fid, nsteps);
}

export function deflate_face(fid: Face, nsteps: number) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_deflate_face');
        f.add_parameter(from(fid));
        f.add_parameter(nsteps);
        cfcd.add_function_call(f);
    }
    return logged_jcore.deflate_face(fid, nsteps);
}

export function make_edges_planar(edge_ids: Array<Edge>, base: Vector3, normal: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (edge_ids.length == 0)
        return true;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_make_edges_planar');
        f.add_parameter(edge_ids.length);
        for (const id of edge_ids)
            f.add_parameter(from(id));
        f.add_parameter(base.x);
        f.add_parameter(base.y);
        f.add_parameter(base.z);
        f.add_parameter(normal.x);
        f.add_parameter(normal.y);
        f.add_parameter(normal.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.make_edges_planar(edge_ids, base, normal);
}

export function pop_face(fid: Face, is_in: boolean, dir_vec: Vector3, dist: number, pop_vector: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_pop_face');
        f.add_parameter(from(fid));
        f.add_parameter(is_in ? 1 : 0);
        if (dir_vec == null) {
            f.add_parameter(0);
        } else {
            f.add_parameter(3);
            f.add_parameter(dir_vec.x);
            f.add_parameter(dir_vec.y);
            f.add_parameter(dir_vec.z);
        }
        f.add_parameter(dist);
        cfcd.add_function_call(f);
    }
    return logged_jcore.pop_face(fid, is_in, dir_vec, dist, pop_vector);
}

export function pop_faces(face_ids: Array<Face>, is_in: boolean, dir_vec: Vector3, dist: number, pop_vector: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_pop_faces');
        f.add_parameter(face_ids.length);
        for (const id of face_ids)
            f.add_parameter(from(id));
        f.add_parameter(is_in ? 1 : 0);
        if (dir_vec == null) {
            f.add_parameter(0);
        } else {
            f.add_parameter(3);
            f.add_parameter(dir_vec.x);
            f.add_parameter(dir_vec.y);
            f.add_parameter(dir_vec.z);
        }
        f.add_parameter(dist);
        cfcd.add_function_call(f);
    }
    return logged_jcore.pop_faces(face_ids, is_in, dir_vec, dist, pop_vector);
}

export function make_hole_or_handle(f1id: Face, f2id: Face, is_hole: boolean, v1id: Vertex, v2id: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_make_hole_or_handle');
        f.add_parameter(from(f1id));
        f.add_parameter(from(f2id));
        f.add_parameter(is_hole ? 1 : 0);
        f.add_parameter(from(v1id));
        f.add_parameter(from(v2id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.make_hole_or_handle(f1id, f2id, is_hole, v1id, v2id);
}

export function make_hole_or_handle_multi(f1ids: Array<Face>, f2ids: Array<Face>, is_hole: boolean, v1id: Vertex, v2id: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_make_hole_or_handle_multi');
        f.add_parameter(f1ids.length);
        for (const id of f1ids)
            f.add_parameter(from(id));
        f.add_parameter(f2ids.length);
        for (const id of f2ids)
            f.add_parameter(from(id));
        f.add_parameter(is_hole ? 1 : 0);
        f.add_parameter(from(v1id));
        f.add_parameter(from(v2id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.make_hole_or_handle_multi(f1ids, f2ids, is_hole, v1id, v2id);
}

// function jcore_make_face_planar(face_id) {
//   const f = new CoreFunctionCallDescription('phi_make_face_planar');
//   f.add_parameter(face_id);
//   cfcd.add_function_call(f);
//   return logged_jcore.make_face_planar(face_id);
// }

export function get_face_curvatures(face_id: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_curvatures');
        f.add_parameter(from(face_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_curvatures(face_id);
}

export function get_face_normals(face_id: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_normals');
        f.add_parameter(from(face_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_normals(face_id);
}

export function translate_faces_to_nurbs(face_ids: Array<Face>) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (face_ids.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_translate_faces_to_nurbs');
        f.add_parameter(face_ids.length);
        for (const id of face_ids)
            f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.translate_faces_to_nurbs(face_ids);
}

export function move_vertex_to_coords(vertex_id: Vertex, x: number, y: number, z: number) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_move_vertex_to_coords');
        f.add_parameter(from(vertex_id));
        f.add_parameter(x);
        f.add_parameter(y);
        f.add_parameter(z);
        f.add_parameter(uid_get_ball_size());
        f.add_parameter(uid_get_ball_hardness());
        cfcd.add_function_call(f);
    }
    return logged_jcore.move_vertex_to_coords(vertex_id, x, y, z);
}

export function is_edge_linear(eid: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_edge_linear');
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_edge_linear(eid);
}

export function is_edge_linear_geometry(eid: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_edge_linear_geometry');
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_edge_linear_geometry(eid);
}

export function set_linearity_of_edges(ids_of_edges: Array<Edge>, make_linear: boolean, force: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (ids_of_edges.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription(
            make_linear
                ? 'phi_make_edges_linear'
                : 'phi_make_edges_nonlinear'
        );
        f.add_parameter(ids_of_edges.length);
        for (const id of ids_of_edges)
            f.add_parameter(from(id));
        if (make_linear)
            f.add_parameter(force ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_linearity_of_edges(ids_of_edges, make_linear, force);
}

export function make_edges_circular(edge_ids: Array<Edge>, vertex_ids: Array<Vertex>, center_vertex_id: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_make_edges_circular');
        f.add_parameter(edge_ids.length);
        for (const id of edge_ids)
            f.add_parameter(from(id));
        f.add_parameter(vertex_ids.length);
        for (const id of vertex_ids)
            f.add_parameter(from(id));
        f.add_parameter(from(center_vertex_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.make_edges_circular(edge_ids, vertex_ids, center_vertex_id);
}

export function make_face_circular(face_id: Face) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_make_face_circular');
        f.add_parameter(from(face_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.make_face_circular(face_id);
}

export function can_make_edges_symmetric(e1: Edge, e2: Edge, b: Vector3, n: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_make_edges_symmetric');
        f.add_parameter(from(e1));
        f.add_parameter(from(e2));
        f.add_parameter(b.x);
        f.add_parameter(b.y);
        f.add_parameter(b.z);
        f.add_parameter(n.x);
        f.add_parameter(n.y);
        f.add_parameter(n.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_make_edges_symmetric(e1, e2, b, n);
}

export function make_edges_symmetric(e1: Edge, e2: Edge, b: Vector3, n: Vector3, dont_affect_modified_objects = false) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_make_edges_symmetric');
        f.add_parameter(from(e1));
        f.add_parameter(from(e2));
        f.add_parameter(b.x);
        f.add_parameter(b.y);
        f.add_parameter(b.z);
        f.add_parameter(n.x);
        f.add_parameter(n.y);
        f.add_parameter(n.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.make_edges_symmetric(e1, e2, b, n, dont_affect_modified_objects);
}

export function triangulate_faces(face_ids: Array<Face>, triangles_per_face: number) {
    if (face_ids.length == 0)
        return true;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_triangulate_faces');
        f.add_parameter(face_ids.length);
        for (const id of face_ids)
            f.add_parameter(from(id));
        f.add_parameter(triangles_per_face);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.triangulate_faces(face_ids, triangles_per_face);
}

export function set_display_face_triangulation_res(triangles_per_face: number) {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_display_face_triangulation_res');
        f.add_parameter(triangles_per_face);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_display_face_triangulation_res(triangles_per_face);
}

export function get_display_face_triangulation_res() {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_display_face_triangulation_res');
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_display_face_triangulation_res();
}

export function set_display_face_triangulation_res_for_drag(triangles_per_face: number) {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_display_face_triangulation_res_for_drag');
        f.add_parameter(triangles_per_face);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_display_face_triangulation_res_for_drag(triangles_per_face);
}

export function get_display_face_triangulation_res_for_drag() {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_display_face_triangulation_res_for_drag');
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_display_face_triangulation_res_for_drag();
}

export function set_face_tessel(on: boolean) {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_face_tessel');
        f.add_parameter(on ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_face_tessel(on);
}

export function set_global_fairing_strength(strength: number) {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_global_fairing_strength');
        f.add_parameter(strength);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_global_fairing_strength(strength);
}

export function get_global_fairing_strength() {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_global_fairing_strength');
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_global_fairing_strength();
}

export function get_point_at_edge_param(eid: Edge, u: number) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_point_at_edge_param');
        f.add_parameter(from(eid));
        f.add_parameter(u);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_point_at_edge_param(eid, u);
}

export function get_tangent_at_edge_param(eid: Edge, u: number) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_tangent_at_edge_param');
        f.add_parameter(from(eid));
        f.add_parameter(u);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_tangent_at_edge_param(eid, u);
}

export function is_object_fixed(id: Vertex | Edge | Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_object_fixed');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_object_fixed(id);
}

export function change_object_fixedness(ids: Array<Vertex | Edge | Face>, fixed: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_change_object_fixedness');
        f.add_parameter(ids.length);
        for (const id of ids)
            f.add_parameter(from(id));
        f.add_parameter(fixed ? 1 : 0);
        cfcd.add_function_call(f);
    }

    return logged_jcore.change_object_fixedness(ids, fixed);
}

export function is_object_visible(id: Vertex | Edge | Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_object_visible');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_object_visible(id);
}

export function change_object_visibility(ids: Array<Vertex | Edge | Face>, visible: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_change_object_visibility');
        f.add_parameter(ids.length);
        for (const id of ids)
            f.add_parameter(from(id));
        f.add_parameter(visible ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.change_object_visibility(ids, visible);
}

export function calc_ray_dir_for_face_sampling(f_id: Face, v0_id: Vertex, v1_id: Vertex, linedir: Vector3) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_calc_ray_dir_for_face_sampling');
        f.add_parameter(from(f_id));
        f.add_parameter(from(v0_id));
        f.add_parameter(from(v1_id));
        //XXX f.add_parameter(linedir.x);
        //XXX f.add_parameter(linedir.y);
        //XXX f.add_parameter(linedir.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.calc_ray_dir_for_face_sampling(f_id, v0_id, v1_id, linedir);
}

export function set_edge_freedoms(edge_ids: Array<Edge>, freedom: number) {
    if (edge_ids.length == 0)
        return true;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_curve_freedom_of_edges');
        f.add_parameter(edge_ids.length);
        for (const id of edge_ids)
            f.add_parameter(from(id));
        f.add_parameter(freedom);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_edge_freedoms(edge_ids, freedom);
}

export function find_edge_shared_vertex(e1id: Edge, e2id: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_find_edge_shared_vertex');
        f.add_parameter(from(e1id));
        f.add_parameter(from(e2id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.find_edge_shared_vertex(e1id, e2id);
}

export function get_edge_num_of_face(fid: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edge_num_of_face');
        f.add_parameter(from(fid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edge_num_of_face(fid);
}

export function get_edge_num_of_vertex(vid: Vertex) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edge_num_of_vertex');
        f.add_parameter(from(vid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edge_num_of_vertex(vid);
}

export function get_edges_of_face(fid: Face, ne: number) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edges_of_face');
        f.add_parameter(from(fid));
        f.add_parameter(ne);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edges_of_face(fid, ne);
}

export function get_edges_of_vertex(vid: Vertex, ne: number) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edges_of_vertex');
        f.add_parameter(from(vid));
        f.add_parameter(ne);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edges_of_vertex(vid, ne);
}

export function get_faces_of_edge(eid: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_faces_of_edge');
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_faces_of_edge(eid);
}

export function get_vertices_of_edge(eid: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_vertices_of_edge');
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_vertices_of_edge(eid);
}

export function is_vertex_of_face(vid: Vertex, fid: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_vertex_of_face');
        f.add_parameter(from(vid));
        f.add_parameter(from(fid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_vertex_of_face(vid, fid);
}

export function get_vertex_normal(vid: Vertex) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_vertex_normal');
        f.add_parameter(from(vid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_vertex_normal(vid);
}

/*
function jcore_get_edge_end_tangents(eid) {
  if (LOGGING_INFO_ON) {
    const f = new CoreFunctionCallDescription('phi_get_edge_end_tangents');
    f.add_parameter(eid);
    cfcd.add_function_call(f);
  }
  return logged_jcore.get_edge_end_tangents(eid);
}
*/

export function get_sedge_num_of_face(face_id: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_sedge_num_of_face');
        f.add_parameter(from(face_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_sedge_num_of_face(face_id);
}

export function get_face_superedges(face_id: Face, ne: number, nse: number) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_sedges_of_face');
        f.add_parameter(from(face_id));
        f.add_parameter(0); // dummy for array pointer
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_superedges(face_id, ne, nse);
}

export function get_face_crosshair_at_params(face_id: Face, u: number, v: number, edge_ids: Array<Edge>, coords: Vector3[]) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_crosshair_at_params');
        f.add_parameter(from(face_id));
        f.add_parameter(u);
        f.add_parameter(v);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_crosshair_at_params(face_id, u, v, edge_ids, coords);
}

export function get_num_of_model_bodies() {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_body_num_of_model');
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_num_of_model_bodies();
}

export function get_model_bodies(nbodies: number) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_model_bodies');
        f.add_parameter(nbodies);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_model_bodies(nbodies);
}

export function is_body_open(bid: Body) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_body_open');
        f.add_parameter(from(bid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_body_open(bid);
}

export function get_body_of_vertex(id: Vertex) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_body_of_vertex');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_body_of_vertex(id);
}

export function get_body_of_edge(id: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_body_of_edge');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_body_of_edge(id);
}

export function get_body_of_face(id: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_body_of_face');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_body_of_face(id);
}

export function save_model(extra_data_string: string, scale_fac: number) {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_save_model');
        f.add_parameter(extra_data_string);
        f.add_parameter(extra_data_string.length);
        f.add_parameter(scale_fac);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.save_model(extra_data_string, scale_fac);
}

export function get_world_scaling_factor() {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_world_scaling_factor');
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_world_scaling_factor();
}

export function set_world_scaling_factor(factor: number) {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_world_scaling_factor');
        f.add_parameter(factor);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_world_scaling_factor(factor);
}

export function set_add_edge_pair_tangencies_on_load(on: boolean) {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_add_edge_pair_tangencies_on_load');
        f.add_parameter(on ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_add_edge_pair_tangencies_on_load(on);
}

export function can_undo() {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_undo');
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_undo();
}

export function can_redo() {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_redo');
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_redo();
}

export function can_inflate(fid: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_inflate');
        f.add_parameter(from(fid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_inflate(fid);
}

export function can_deflate(fid: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_deflate');
        f.add_parameter(from(fid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_deflate(fid);
}

export function is_vertex_of_edge(vid: Vertex, eid: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_is_vertex_of_edge');
        f.add_parameter(from(vid));
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.is_vertex_of_edge(vid, eid);
}

export function can_make_edges_circular(edge_ids: Array<Edge>, vertex_ids: Array<Vertex>, center_vertex_id: Vertex) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_make_edges_circular');
        f.add_parameter(edge_ids.length);
        for (const id of edge_ids)
            f.add_parameter(from(id));
        f.add_parameter(vertex_ids.length);
        for (const id of vertex_ids)
            f.add_parameter(from(id));
        f.add_parameter(from(center_vertex_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_make_edges_circular(edge_ids, vertex_ids, center_vertex_id);
}

export function can_make_face_circular(fid: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_can_make_face_circular');
        f.add_parameter(from(fid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.can_make_face_circular(fid);
}

export function get_edge_freedom(eid: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edge_freedom');
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edge_freedom(eid);
}

export function get_edge_length(eid: Edge) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edge_length');
        f.add_parameter(from(eid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edge_length(eid);
}

export function set_face_material(face_ids: Array<Face>, material_string: string) {
    if (face_ids.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_face_material');
        f.add_parameter(face_ids.length);
        for (const fid of face_ids)
            f.add_parameter(from(fid));
        f.add_parameter(material_string);
        f.add_parameter(material_string.length);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_face_material(face_ids, material_string);
}

export function get_face_material(face_id: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_material');
        f.add_parameter(from(face_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_material(face_id);
}

export function set_body_label(body_ids: Array<Body>, label_string: string) {
    if (body_ids.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_body_label');
        f.add_parameter(body_ids.length);
        for (const bid of body_ids)
            f.add_parameter(from(bid));
        f.add_parameter(label_string);
        f.add_parameter(label_string.length);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_body_label(body_ids, label_string);
}

export function get_body_label(body_id: Body) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_body_label');
        f.add_parameter(from(body_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_body_label(body_id);
}

export function set_face_label(face_ids: Array<Face>, label_string: string) {
    if (face_ids.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_face_label');
        f.add_parameter(face_ids.length);
        for (const fid of face_ids)
            f.add_parameter(from(fid));
        f.add_parameter(label_string);
        f.add_parameter(label_string.length);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_face_label(face_ids, label_string);
}

export function get_face_label(face_id: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_label');
        f.add_parameter(from(face_id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_label(face_id);
}

export function set_face_color(face_ids: Array<Face>, r: number, g: number, b: number) {
    if (face_ids.length == 0)
        return true;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_face_colour');
        f.add_parameter(face_ids.length);
        for (const fid of face_ids)
            f.add_parameter(from(fid));
        f.add_parameter(r);
        f.add_parameter(g);
        f.add_parameter(b);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_face_color(face_ids, r, g, b);
}

export function get_face_color(face_id: Face) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_colour');
        f.add_parameter(from(face_id));
        f.add_parameter(0);
        f.add_parameter(0);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }
    const wv_r = new WrappedValue(0);
    const wv_g = new WrappedValue(0);
    const wv_b = new WrappedValue(0);
    const res = logged_jcore.get_face_color(face_id, wv_r, wv_g, wv_b);
    if (!res || wv_r.v < 0 || wv_g.v < 0 || wv_b.v < 0)
        return null;
    return rgbToHex(wv_r.v, wv_g.v, wv_b.v);
}

export function copy_body(id: Body) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_copy_body');
        f.add_parameter(from(id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.copy_body(id);
}

export function get_edge_curvatures(edge_id: Edge, no_of_samples: number) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edge_curvatures');
        f.add_parameter(from(edge_id));
        f.add_parameter(no_of_samples);
        f.add_parameter(0);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edge_curvatures(edge_id, no_of_samples);
}

export function merge_bodies_on_edge(b1id: Body, b2id: Body, e1id: Edge, e2id: Edge, v1id: Vertex, v2id: Vertex) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_merge_bodies_on_edge');
        f.add_parameter(from(b1id));
        f.add_parameter(from(b2id));
        f.add_parameter(from(e1id));
        f.add_parameter(from(e2id));
        f.add_parameter(from(v1id));
        f.add_parameter(from(v2id));
        cfcd.add_function_call(f);
    }
    return logged_jcore.merge_bodies_on_edge(b1id, b2id, e1id, e2id, v1id, v2id);
}

export function flip_body_inside_out(bid: Body) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_flip_body_inside_out');
        f.add_parameter(from(bid));
        cfcd.add_function_call(f);
    }
    return logged_jcore.flip_body_inside_out(bid);
}

export function set_auto_add_tangencies(on: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_auto_add_tangencies');
        f.add_parameter(on ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_auto_add_tangencies(on);
}

export function get_face_triangulation_info(face_id: Face, wv_ncoords: WrappedValue<number>, wv_ntriangles: WrappedValue<number>) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_triangulation_info');
        f.add_parameter(from(face_id));
        f.add_parameter(0);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_triangulation_info(face_id, wv_ncoords, wv_ntriangles);
}

export function get_face_triangulation(face_id: Face, ncoords: number, ntriangles: number, coords: number[], triangles: number[]) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_face_triangulation');
        f.add_parameter(from(face_id));
        f.add_parameter(ncoords);
        f.add_parameter(ntriangles);
        f.add_parameter(0);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_face_triangulation(face_id, ncoords, ntriangles, coords, triangles);
}

export function get_edge_planes_from_point_intersection(edge_id: Edge, vertex_coords: Vector3, is_high_res: boolean, wv_tx: WrappedValue<number>, wv_ty: WrappedValue<number>, wv_tz: WrappedValue<number>) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edge_planes_from_point_intersection');
        f.add_parameter(from(edge_id));
        f.add_parameter(vertex_coords.x);
        f.add_parameter(vertex_coords.y);
        f.add_parameter(vertex_coords.z);
        f.add_parameter(is_high_res ? 1 : 0);
        f.add_parameter(0);
        f.add_parameter(0);
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edge_planes_from_point_intersection(edge_id, vertex_coords, is_high_res, wv_tx, wv_ty, wv_tz);
}

export function set_edge_tangent_at_vertex(edge_id: Edge, vertex_id: Vertex, tan: Vector3) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_edge_tangent_at_vertex');
        f.add_parameter(from(edge_id));
        f.add_parameter(from(vertex_id));
        f.add_parameter(tan.x);
        f.add_parameter(tan.y);
        f.add_parameter(tan.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_edge_tangent_at_vertex(edge_id, vertex_id, tan);
}

export function get_edge_tangent_at_vertex(edge_id: Edge, vertex_id: Vertex, tan: Vector3) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edge_tangent_at_vertex');
        f.add_parameter(from(edge_id));
        f.add_parameter(from(vertex_id));
        f.add_parameter(tan.x);
        f.add_parameter(tan.y);
        f.add_parameter(tan.z);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edge_tangent_at_vertex(edge_id, vertex_id, tan);
}

export function set_edge_fixing_of_tangent_at_vertex(edge_id: Edge, vertex_id: Vertex, must_fix: boolean) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_edge_fixing_of_tangent_at_vertex');
        f.add_parameter(from(edge_id));
        f.add_parameter(from(vertex_id));
        f.add_parameter(must_fix ? 1 : 0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.set_edge_fixing_of_tangent_at_vertex(edge_id, vertex_id, must_fix);
}

export function get_edge_fixing_of_tangent_at_vertex(edge_id: Edge, vertex_id: Vertex, wv_is_fixed: WrappedValue<1 | 0>) {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_edge_fixing_of_tangent_at_vertex');
        f.add_parameter(from(edge_id));
        f.add_parameter(from(vertex_id));
        f.add_parameter(wv_is_fixed.v);
        cfcd.add_function_call(f);
    }
    return logged_jcore.get_edge_fixing_of_tangent_at_vertex(edge_id, vertex_id, wv_is_fixed);
}

export function stop_undo_tracking() {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_undo_tracking');
        f.add_parameter(0);
        cfcd.add_function_call(f);
    }
    return logged_jcore.stop_undo_tracking();
}

export function start_undo_tracking() {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_undo_tracking');
        f.add_parameter(1);
        cfcd.add_function_call(f);
    }
    return logged_jcore.start_undo_tracking();
}

export function save_undo_state() {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_save_undo_state');
        cfcd.add_function_call(f);
    }
    return logged_jcore.save_undo_state();
}

export function start_compound_operation() {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_start_compound_operation');
        cfcd.add_function_call(f);
    }
    return logged_jcore.start_compound_operation();
}

export function end_compound_operation() {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_end_compound_operation');
        cfcd.add_function_call(f);
    }
    return logged_jcore.end_compound_operation();
}

export function get_debug_variables() {
    if (LOGGING_INFO_ON) {
        const f = new CoreFunctionCallDescription('phi_get_debug_variables');
        cfcd.add_function_call(f);
    }
    // @ts-ignore
    return logged_jcore_get_debug_variables();
}

export function set_debug_variables(str: string) {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_debug_variables');
        f.add_parameter(str);
        cfcd.add_function_call(f);
    }
    // @ts-ignore
    return logged_jcore_set_debug_variables(str);
}

export function undo() {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_undo');
        cfcd.add_function_call(f);
    }
    return logged_jcore.undo();
}

export function redo() {
    if (!check_that_user_subscription_is_valid())
        return;
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_redo');
        cfcd.add_function_call(f);
    }
    return logged_jcore.redo();
}

export function do_magic(vertices: Array<Vertex>, edges: Array<Edge>, faces: Array<Face>, numbers: number[]) {
    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_do_magic');
        f.add_parameter(vertices.length);
        for (const id of vertices)
            f.add_parameter(from(id));
        f.add_parameter(edges.length);
        for (const id of edges)
            f.add_parameter(from(id));
        f.add_parameter(faces.length);
        for (const id of faces)
            f.add_parameter(from(id));
        f.add_parameter(numbers.length);
        for (const n of numbers)
            f.add_parameter(n);
        cfcd.add_function_call(f);
    }
    // @ts-ignore
    return logged_jcore_do_magic(vertices, edges, faces, numbers);
}

export function set_save_model_compression_level(clevel: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
    if (!check_that_user_subscription_is_valid())
        return;

    if (LOGGING_ACTION_ON) {
        const f = new CoreFunctionCallDescription('phi_set_save_model_compression_level');
        f.add_parameter(clevel);
        cfcd.add_function_call(f);
    }
    logged_jcore.set_save_model_compression_level(clevel);
}
